import { useState, useEffect, useMemo, useCallback } from 'react';
import { Copy, RefreshCw, Shield, ShieldCheck } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Slider } from '@/components/ui/Slider';
import { Field } from '@/components/ui/Input';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';

const SETS = {
  lower: 'abcdefghijklmnopqrstuvwxyz',
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  digits: '0123456789',
  symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};
const AMBIGUOUS = /[O0Il1|`'"]/g;

function gen(length: number, opts: typeof DEFAULTS): string {
  let pool = '';
  if (opts.lower) pool += SETS.lower;
  if (opts.upper) pool += SETS.upper;
  if (opts.digits) pool += SETS.digits;
  if (opts.symbols) pool += SETS.symbols;
  if (opts.excludeAmbiguous) pool = pool.replace(AMBIGUOUS, '');
  if (!pool) return '';

  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < length; i++) out += pool[arr[i] % pool.length];

  // Ensure at least one of each enabled set
  const required: string[] = [];
  if (opts.lower) required.push(pickFrom(SETS.lower, opts.excludeAmbiguous));
  if (opts.upper) required.push(pickFrom(SETS.upper, opts.excludeAmbiguous));
  if (opts.digits) required.push(pickFrom(SETS.digits, opts.excludeAmbiguous));
  if (opts.symbols) required.push(pickFrom(SETS.symbols, opts.excludeAmbiguous));

  let arr2 = out.split('');
  required.forEach((c, i) => {
    if (c) arr2[i] = c;
  });
  // Shuffle
  for (let i = arr2.length - 1; i > 0; i--) {
    const j = (crypto.getRandomValues(new Uint32Array(1))[0] | 0) % (i + 1);
    [arr2[i], arr2[j]] = [arr2[j], arr2[i]];
  }
  return arr2.join('');
}

function pickFrom(set: string, exclAmb: boolean): string {
  const cleaned = exclAmb ? set.replace(AMBIGUOUS, '') : set;
  if (!cleaned) return '';
  return cleaned[crypto.getRandomValues(new Uint32Array(1))[0] % cleaned.length];
}

const DEFAULTS = {
  lower: true,
  upper: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: false,
};

function entropyBits(length: number, opts: typeof DEFAULTS): number {
  let pool = 0;
  if (opts.lower) pool += 26;
  if (opts.upper) pool += 26;
  if (opts.digits) pool += 10;
  if (opts.symbols) pool += 26;
  if (opts.excludeAmbiguous) pool = Math.max(0, pool - 8);
  return Math.round(length * Math.log2(Math.max(2, pool)));
}

function strengthLabel(bits: number): { label: string; color: string; pct: number } {
  if (bits < 28) return { label: 'Very weak', color: 'bg-danger', pct: 15 };
  if (bits < 40) return { label: 'Weak', color: 'bg-warning', pct: 30 };
  if (bits < 60) return { label: 'Reasonable', color: 'bg-warning', pct: 50 };
  if (bits < 80) return { label: 'Strong', color: 'bg-success', pct: 75 };
  if (bits < 100) return { label: 'Very strong', color: 'bg-success', pct: 90 };
  return { label: 'Excellent', color: 'bg-accent', pct: 100 };
}

export function PasswordGenerator() {
  const [length, setLength] = useState(20);
  const [opts, setOpts] = useState(DEFAULTS);
  const [pw, setPw] = useState('');

  const regenerate = useCallback(() => setPw(gen(length, opts)), [length, opts]);

  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const bits = useMemo(() => entropyBits(length, opts), [length, opts]);
  const strength = strengthLabel(bits);

  return (
    <div>
      <ToolHeader
        toolId="password-generator"
        title="Password generator"
        description="Cryptographically secure passwords with entropy estimate. Built on Web Crypto."
      />

      {/* Hero password */}
      <Card className="!p-6 mb-4 relative overflow-hidden">
        <div
          className={cn(
            'absolute inset-x-0 top-0 h-1 transition-all',
            strength.color,
          )}
          style={{ width: `${strength.pct}%` }}
        />
        <div className="font-mono text-2xl break-all min-h-[3rem]">{pw || '…'}</div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs">
              {bits >= 60 ? (
                <ShieldCheck size={14} className="text-success" />
              ) : (
                <Shield size={14} className="text-warning" />
              )}
              <span className="font-medium">{strength.label}</span>
            </span>
            <span className="text-2xs text-fg-subtle font-mono">~{bits} bits of entropy</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={regenerate}>
              <RefreshCw size={12} />
              Regenerate
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const ok = await copyToClipboard(pw);
                if (ok) toast.success('Password copied');
              }}
            >
              <Copy size={12} />
              Copy
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <Field label="Length" hint={`${length} chars`}>
            <Slider
              value={length}
              onChange={setLength}
              min={6}
              max={64}
              showValue={false}
            />
          </Field>
        </Card>
        <Card className="space-y-2.5">
          <ToggleRow
            label="Lowercase (a-z)"
            on={opts.lower}
            onChange={(v) => setOpts({ ...opts, lower: v })}
          />
          <ToggleRow
            label="Uppercase (A-Z)"
            on={opts.upper}
            onChange={(v) => setOpts({ ...opts, upper: v })}
          />
          <ToggleRow
            label="Digits (0-9)"
            on={opts.digits}
            onChange={(v) => setOpts({ ...opts, digits: v })}
          />
          <ToggleRow
            label="Symbols (!@#)"
            on={opts.symbols}
            onChange={(v) => setOpts({ ...opts, symbols: v })}
          />
          <div className="border-t border-border pt-2.5">
            <ToggleRow
              label="Exclude ambiguous (Il1O0|`')"
              on={opts.excludeAmbiguous}
              onChange={(v) => setOpts({ ...opts, excludeAmbiguous: v })}
            />
          </div>
        </Card>
      </div>

      {/* Bulk generate */}
      <Card className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Bulk · 6 alternates</h3>
          <Button variant="ghost" size="sm" onClick={regenerate}>
            <RefreshCw size={11} />
            Reroll
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Array.from({ length: 6 }).map((_, i) => {
            const v = gen(length, opts);
            return (
              <button
                key={`${pw}-${i}`}
                onClick={async () => {
                  const ok = await copyToClipboard(v);
                  if (ok) toast.success('Copied');
                }}
                className="px-3 py-2 rounded-lg bg-bg-inset border border-border hover:border-accent hover:bg-accent/5 font-mono text-sm break-all text-left transition"
              >
                {v}
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function ToggleRow({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between text-sm cursor-pointer">
      <span className="text-fg-muted">{label}</span>
      <Switch checked={on} onChange={onChange} />
    </label>
  );
}

export default PasswordGenerator;
